/* eslint-disable react/display-name */
import React, {
    useState,
    useEffect,
    useCallback,
    PropsWithChildren,
    useReducer,
    useMemo,
} from 'react';
import { Button, Radio, Selector, DatePicker, Table, Mark, Pagination } from '@roo/roo';
import { Column } from '@roo/roo/Table';
import dayjs, { Dayjs } from 'dayjs';
import urlUtil from '@/utils/url';
import useUnreplyCount from '@/hooks/use-unreply-count';
import { useGetRequest, usePostRequest } from '@/hooks/use-request';
import { ReviewApi, Review, Service, ReplyStatus, HasText } from './types';
import ReplyModal, { replyModalReducer } from './ReplyModal';
import './index.scss';

// @ts-ignore
const RadioGroup = Radio.Group;

interface ReviewFilterProps<T extends string | number> {
    title: string;
    options: {
        value: T;
        label: string;
    }[];
    value: T;
    onChange: (value: T) => void;
}

interface RooSelectorOption {
    value: string | number;
    label: string;
    disabled?: boolean;
}

function ReviewFilter<T extends string | number>({
    title,
    options,
    onChange,
    value,
}: ReviewFilterProps<T>): React.ReactElement<PropsWithChildren<ReviewFilterProps<T>>> {
    return (
        <div className="review-filter">
            <span className="review-filter__title">{title}</span>
            <RadioGroup value={value} onChange={onChange}>
                {options.map(option => (
                    <Radio key={option.value} value={option.value}>
                        {option.label}
                    </Radio>
                ))}
            </RadioGroup>
        </div>
    );
}

const initialServiceList: RooSelectorOption[] = [{ value: 0, label: '全部' }];

const scoreFilter = [
    { value: 0, label: '全部' },
    { value: 1, label: '1星' },
    { value: 2, label: '2星' },
    { value: 3, label: '3星' },
    { value: 4, label: '4星' },
    { value: 5, label: '5星' },
];

const hasReplyFilter = [
    { value: ReplyStatus.ALL, label: '全部' },
    { value: ReplyStatus.HAS_REPLY, label: '已回复' },
    { value: ReplyStatus.NO_REPLY, label: '未回复' },
];

const hasContentFilter = [
    { value: HasText.ALL, label: '全部' },
    { value: HasText.YES, label: '有内容' },
];

const pageSize = 10;

const Evaluation: React.FC = () => {
    const [serviceScore, setServiceScore] = useState(0);
    const [hasReply, setHasReply] = useState(ReplyStatus.ALL);
    const [hasContent, setHasContent] = useState(HasText.ALL);

    // 服务列表与相应的筛选器
    const [serviceList, setServiveList] = useState<RooSelectorOption[]>(initialServiceList);
    const [serviceID, setServiceID] = useState(0);
    const { data: services } = useGetRequest<{}, Service[]>('/api/service/all/list');
    useEffect(() => {
        if (services) {
            setServiveList([
                ...initialServiceList,
                ...services.map(service => ({
                    value: service.serviceID,
                    label: service.serviceName,
                })),
            ]);
        }
    }, [services]);

    const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>(() => {
        const now = dayjs();
        return [now.subtract(30, 'day'), now];
    });
    const setDateRangeFromTimeStamp = useCallback((range: [number, number]) => {
        setDateRange(range.map(timestamp => dayjs(timestamp)) as [Dayjs, Dayjs]);
    }, []);

    const [pageNum, setPageNum] = useState(1);

    // 评论列表总数
    const { data: total, loading: isTotalLoading } = usePostRequest<
        ReviewApi.GetReviewListTotalNum.Req,
        ReviewApi.GetReviewListTotalNum.Res
    >(
        '/api/evaluate/total',
        () => ({
            score: serviceScore,
            replyStatus: hasReply,
            hasText: hasContent,
            itemId: serviceID,
            startTime: dateRange[0] ? dateRange[0].unix() : dayjs().subtract(30, 'day').unix(),
            endTime: dateRange[1] ? dateRange[1].unix() : dayjs().unix(),
        }),
        [serviceScore, hasReply, hasContent, serviceID, dateRange],
    );

    // 评论列表
    const {
        data: reviewList,
        loading: isReviewListLoading,
        mutate: updateReviewList,
    } = usePostRequest<ReviewApi.GetReviewList.Req, ReviewApi.GetReviewList.Res>(
        '/api/evaluate/list',
        () => ({
            score: serviceScore,
            replyStatus: hasReply,
            hasText: hasContent,
            itemId: serviceID,
            startTime: dateRange[0] ? dateRange[0]?.unix() : dayjs().subtract(30, 'day').unix(),
            endTime: dateRange[1] ? dateRange[1]?.unix() : dayjs().unix(),
            pageNum,
            pageSize,
        }),
        [serviceScore, hasReply, hasContent, serviceID, dateRange, pageNum],
    );

    const loading = isTotalLoading && isReviewListLoading;

    const { mutate: updateUnreplyCount } = useUnreplyCount();

    const [modalState, dispatch] = useReducer(replyModalReducer, {
        isVisible: false,
        review: null,
    });

    const openReplyModal = useCallback((review: Review) => {
        dispatch({ type: 'open', payload: review });
    }, []);

    const closeReplyModal = useCallback(() => {
        dispatch({ type: 'close' });
    }, []);

    const handleSuccess = useCallback(async () => {
        // 回复成功后更新评论列表和未回复的数目
        await Promise.all([updateReviewList(), updateUnreplyCount()]);
    }, [updateReviewList, updateUnreplyCount]);

    const columns: Column<Review>[] = useMemo(
        () => [
            {
                prop: 'score',
                label: '服务评分',
                headAlign: 'left',
                render: (score: number) => <Mark defaultValue={score} disabled />,
            },
            {
                prop: 'commentDetail',
                label: '评价内容',
                width: '40%',
                render: (commentDetail: string, review: Review) => (
                    <>
                        <div>{commentDetail}</div>
                        {review.replyStatus === ReplyStatus.HAS_REPLY && (
                            <div className="review-item-reply-detail">
                                <span>已回复：</span>
                                <span>{review.replyDetail.trim()}</span>
                            </div>
                        )}
                    </>
                ),
            },
            { prop: 'acctLogin', label: '买家' },
            {
                prop: 'serviceName',
                label: '服务名称',
                render: (serviceName: string, review: Review) => (
                    <>
                        <div className="review-item-service-name">{serviceName}</div>
                        <div className="review-item-version-name">{review.versionName}</div>
                    </>
                ),
            },
            {
                prop: 'timestamp',
                label: '评价时间',
                render: (timestamp: number) => (
                    <span>{dayjs.unix(timestamp).format('YYYY-MM-DD HH:mm')}</span>
                ),
            },
            {
                label: '操作',
                render: (_, review: Review) =>
                    review.replyStatus === ReplyStatus.NO_REPLY && (
                        <Button onClick={() => openReplyModal(review)}>回复</Button>
                    ),
            },
        ],
        [openReplyModal],
    );

    // 上报
    useEffect(() => {
        window.LXAnalytics(
            'pageView',
            {
                custom: {
                    supplier_id: urlUtil.commonParams.providerId,
                },
            },
            {},
            'c_waimai_e_fwsc_fwshoutai_pingjiaguanli',
        );
    }, []);

    return (
        <div className="review-management">
            {useMemo(
                () => (
                    <ReviewFilter<number>
                        title="服务评分"
                        options={scoreFilter}
                        value={serviceScore}
                        onChange={setServiceScore}
                    />
                ),
                [serviceScore],
            )}
            {useMemo(
                () => (
                    <ReviewFilter<number>
                        title="评价回复"
                        options={hasReplyFilter}
                        value={hasReply}
                        onChange={setHasReply}
                    />
                ),
                [hasReply],
            )}
            {useMemo(
                () => (
                    <ReviewFilter<number>
                        title="评价内容"
                        options={hasContentFilter}
                        value={hasContent}
                        onChange={setHasContent}
                    />
                ),
                [hasContent],
            )}
            <div className="review-filter">
                <span className="review-filter__title">服务名称</span>
                <Selector options={serviceList} value={serviceID} onChange={setServiceID} />
            </div>

            <div className="review-filter">
                <span className="review-filter__title">评价日期</span>
                {useMemo(
                    () => (
                        <DatePicker.RangePicker
                            clearable
                            value={dateRange}
                            onChange={setDateRangeFromTimeStamp}
                        />
                    ),
                    [dateRange, setDateRangeFromTimeStamp],
                )}
            </div>

            {reviewList?.length === 0 && !loading ? (
                <div className="review-list review-list--empty">
                    <img
                        alt="empty-review-list"
                        src="https://s3plus.meituan.net/v1/mss_0e4451a82e1d428f9fc3b99c0b3ebf15/pic-manager/f2883475d7659e041aa5b55be63ac9fe/empty-box.png"
                    />
                    <div>当前没有买家评价</div>
                </div>
            ) : (
                <>
                    <div className="review-list">
                        <Table columns={columns} data={reviewList || []} loading={loading} />
                    </div>
                    <div className="review-list-pagination">
                        <Pagination
                            total={total}
                            pageSize={pageSize}
                            currentPage={pageNum}
                            onChange={setPageNum}
                        />
                        <span className="review-list-total-num">共{total}条评价</span>
                    </div>
                </>
            )}

            <ReplyModal
                isVisible={modalState.isVisible}
                review={modalState.review}
                onSuccess={handleSuccess}
                onCancel={closeReplyModal}
            />
        </div>
    );
};

export default Evaluation;
